<template>
  <b-card class="rounded-0">
    <h4 class="text-center">{{ $t('message.signUp.title') }}</h4>
    <b-alert
      :variant="notification.variant"
      class="mt-4 p-4 mx-auto"
      dismissible
      :show="notification.isVisible"
      @dismissed="notification.isVisible=false">
      <i18n path="error.signUp.submit" tag="div">
        <b-link
          href="mailto:support@meine-vertraege.net"
          place="supportLink">{{ $t('message.support.email') }}
        </b-link>
      </i18n>
    </b-alert>
    <b-form
      class="mt-3 p-3"
      @submit.prevent="submit">
      <b-form-group
        :state="$v.regData.salutation.$error ? false : null"
        :invalid-feedback="$t('error.signUp.salutation')">
        <label>
          {{ $t('message.signUp.label.salutation') }}
          <span class="text-danger">*</span>
        </label>
        <b-form-radio-group
          v-model="regData.salutation"
          :options="salutations"
          :state="$v.regData.salutation.$error ? false : null"
          v-on:change="$v.regData.salutation.$touch()">
        </b-form-radio-group>
      </b-form-group>
      <b-form-row class="mt-4">
        <b-col>
          <b-form-group
            :state="$v.regData.firstName.$error ? false : null"
            :invalid-feedback="$t('error.signUp.firstName')">
            <label>
              {{ $t('message.signUp.label.firstName') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              class="rounded-0"
              v-model="regData.firstName"
              :state="$v.regData.firstName.$error ? false : null"
              v-on:change="$v.regData.firstName.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            :state="$v.regData.lastName.$error ? false : null"
            :invalid-feedback="$t('error.signUp.lastName')">
            <label>
              {{ $t('message.signUp.label.lastName') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              class="rounded-0"
              v-model="regData.lastName"
              :state="$v.regData.lastName.$error ? false : null"
              v-on:change="$v.regData.lastName.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row
        v-if="invitation">
        <b-col>
          <b-form-group
            id="signup-relation"
            label-for="signup-relation"
            :state="$v.regData.relation.$error ? false : null"
            :invalid-feedback="$t('error.signUp.relation')">
            <div slot="label">
              {{ relationText }}
              <span class="text-danger">*</span>
            </div>
            <b-form-select
              class="rounded-0"
              v-model="regData.relation"
              :options="relationTypes">
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row class="mt-2">
        <b-col>
          <b-form-group
            :state="$v.regData.email.$error ? false : null"
            :invalid-feedback="$t('error.signUp.email')">
            <label>
              {{ $t('message.signUp.label.email') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              id="email"
              class="rounded-0"
              v-model="regData.email"
              :state="$v.regData.email.$error ? false : null"
              v-on:change="$v.regData.email.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row class="mt-2">
        <b-col>
          <b-form-group
            :state="$v.regData.password.$error ? false : true"
            :invalid-feedback="$t('error.signUp.password')">
            <label>
              {{ $t('message.signUp.label.password') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              class="rounded-0"
              type="password"
              v-model="regData.password"
              :state="$v.regData.password.$error ? false : null"
              @textinput="$v.regData.password.$touch()">
            </b-form-input>
            <div slot="valid-feedback" class="feedback-color">
              {{ $t('error.signUp.password') }}
            </div>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row class="mt-2">
        <b-col>
          <b-form-group
            :state="$v.regData.passwordConfirm.$error ? false : null"
            :invalid-feedback="$t('error.signUp.passwordConfirm')">
            <label>
              {{ $t('message.signUp.label.passwordConfirm') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              class="rounded-0"
              type="password"
              v-model="regData.passwordConfirm"
              :state="$v.regData.passwordConfirm.$error ? false : null"
              v-on:change="$v.regData.passwordConfirm.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row
        v-if="!invitation"
        class="mt-2">
        <b-col>
          <b-form-group
            :state="$v.regData.dateOfBirth.$error ? false : null"
            :invalid-feedback="$t('error.signUp.dateOfBirth')">
            <label>
              {{ $t('message.signUp.label.dateOfBirth') }}
              <span class="text-danger">*</span>
            </label>
            <b-input-group>
              <b-form-input
                class="rounded-0"
                v-model="regData.dateOfBirth"
                :state="$v.regData.dateOfBirth.$error ? false : null"
                @textinput="$v.regData.dateOfBirth.$touch()">
              </b-form-input>
              <b-input-group-text slot="append" class="rounded-0">
                <span class="ti-calendar"></span>
              </b-input-group-text>
            </b-input-group>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row
        v-if="!invitation"
        class="mt-2">
        <b-col>
          <b-form-group
            :state="$v.regData.contractNumber.$error ? false : null"
            :invalid-feedback="$t('error.signUp.contractNumber')">
            <label>
              {{ $t('message.signUp.label.contractNumber') }}
              <span class="text-danger">*</span>
            </label>
            <b-form-input
              class="rounded-0"
              v-model="regData.contractNumber"
              :state="$v.regData.contractNumber.$error ? false : null"
              v-on:change="$v.regData.contractNumber.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-form-row>

      <b-form-row class="mt-3">
        <b-col>
          <b-form-group
            :state="$v.regData.acceptTerms.$error ? false : null"
            :invalid-feedback="$t('error.signUp.acceptTerms')">
            <b-form-checkbox
              v-model="regData.acceptTerms"
              :state="$v.regData.acceptTerms.$error ? false : null"
              v-on:change="$v.regData.acceptTerms.$touch()">
              <i18n path="message.signUp.termsAndPolicies" tag="p">
                <router-link to="/terms" place="terms">{{ $t('message.signUp.terms') }}</router-link>
                <router-link to="/policies" place="privacyPolicies">{{ $t('message.signUp.privacyPolicies') }}
                </router-link>
              </i18n>
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-form-row>
      <b-form-row>
        <b-col>
          <b-btn
            variant="primary"
            class="w-100 p-3 rounded-0"
            type="submit"
            :disabled="$v.$invalid">{{ $t('message.signUp.btn.submit') }}
          </b-btn>
        </b-col>
      </b-form-row>
      <b-form-row class="mt-4">
        <b-col class="text-center">
          <i18n path="message.signUp.signIn" tag="p">
            <router-link to="/signin" place="signInLink">{{ $t('message.signUp.signInHere') }}</router-link>
          </i18n>
        </b-col>
      </b-form-row>
    </b-form>
  </b-card>
</template>

<script>
  import {email, numeric, required, sameAs} from 'vuelidate/lib/validators'
  import utils from '@/utils'

  export default {
    created () {
      this.regData.roles = [{
        roleOfUser: 'POLICY_HOLDER'
      }]
      const invitation = this.$store.getters.invitation
      if (Object.keys(invitation).length !== 0) {
        this.regData.salutation = invitation.contactPersonSalutation
        this.regData.firstName = invitation.contactPersonFirstname
        this.regData.lastName = invitation.contactPersonLastname
        this.regData.email = invitation.contactPersonEmail
        this.regData.invitationUuid = invitation.uuid
        this.regData.invitationEmail = invitation.contactPersonEmail
        this.regData.invitationInviterId = invitation.policyHolderId
        this.regData.relation = this.$store.getters.relationTypes[0]
        this.regData.roles = [{
          roleOfUser: 'CONTACT_PERSON'
        }]
      }
    },
    computed: {
      invitation () {
        const invitation = this.$store.getters.invitation
        if (Object.keys(invitation).length === 0) {
          return undefined
        }
        return invitation
      },
      relationText () {
        return this.$t('message.signUp.label.relation', {
          firstName: this.invitation.policyHolderFirstName,
          lastName: this.invitation.policyHolderLastName,
          salutation: this.invitation.policyHolderSalutation ? this.$t('enums.salutations.' + this.invitation.policyHolderSalutation) : undefined
        })
      },
      relationTypes () {
        return this.$store.getters.relationTypes.map((relationType) => {
          return {
            value: relationType,
            text: this.$t('enums.relationTypes.' + relationType)
          }
        })
      }
    },
    data () {
      return {
        notification: {
          isVisible: false,
          variant: 'danger'
        },
        regData: {},
        salutations: [
          {text: this.$t('message.signUp.radio.mister'), value: 'MR'},
          {text: this.$t('message.signUp.radio.miss'), value: 'MRS'}
        ]
      }
    },
    methods: {
      submit () {
        this.$v.$touch()
        if (!this.$v.$invalid) {
          const data = {...this.regData}
          if (this.regData.dateOfBirth) {
            data.dateOfBirth = this.$moment(this.regData.dateOfBirth, 'DD.MM.YYYY').format('YYYY-MM-DD')
          }
          this.$store.dispatch('signUp', data)
            .then(() => {
              this.$router.push({path: '/signup/confirm', query: {email: this.regData.email}})
            })
            .catch(() => {
              this.notification.isVisible = true
            })
        }
      }
    },
    validations: {
      regData: {
        acceptTerms: {
          required
        },
        dateOfBirth: {
          required () {
            if (this.invitation) {
              return true
            } else {
              return required
            }
          },
          isValidDate: utils.validators.isValidDate
        },
        email: {
          email,
          required
        },
        firstName: {
          isCharactersOnly: utils.validators.isCharactersOnly,
          required
        },
        contractNumber: {
          required () {
            if (this.invitation) {
              return true
            } else {
              return required
            }
          },
          isValidContractNumber (value) {
            if (this.invitation) {
              return true
            } else {
              return utils.validators.isValidContractNumber(value, this)
            }
          },
          isNumeric (value) {
            if (this.invitation) {
              return true
            } else {
              return numeric(value)
            }
          }
        },
        lastName: {
          isCharactersOnly: utils.validators.isCharactersOnly,
          required
        },
        password: {
          required,
          isValidPassword: utils.validators.isValidPassword
        },
        relation: {
          required () {
            if (this.invitation) {
              return required
            } else {
              return true
            }
          }
        },
        salutation: {
          required
        },
        passwordConfirm: {
          required,
          sameAsPassword: sameAs('password')
        }
      }
    }
  }
</script>

<style scoped>
  .feedback-color {
    color: grey
  }
</style>



// WEBPACK FOOTER //
// src/components/auth/SignUp.vue